import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { Auth } from 'aws-amplify';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import CentralCalls from '../../centralCalls';
import FileUpload from '../FileUpload';
import logo from '../../logo.webp';

function parseJsonValue(value) {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function normalizeProcedureResult(raw) {
  if (Array.isArray(raw) && raw.length === 1 && raw[0] && typeof raw[0] === 'object' && 'json' in raw[0]) {
    return parseJsonValue(raw[0].json);
  }

  if (raw && typeof raw === 'object' && 'json' in raw) {
    return parseJsonValue(raw.json);
  }

  return raw;
}

function getPhaseStatusOid(item) {
  return item?.phase_status_oid ?? item?.phaseStatusOid ?? item?.phase_status_oid_val ?? item?.oid ?? null;
}

function getDateScheduledKey(item) {
  return toIsoDateString(item?.date_scheduled ?? item?.dateScheduled ?? '') || '';
}

function parseArrayOrderValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => Number(item)).filter((item) => Number.isFinite(item));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => Number(item)).filter((item) => Number.isFinite(item));
      }
    } catch {
      return trimmed
        .replace(/^\[/, '')
        .replace(/\]$/, '')
        .split(',')
        .map((item) => Number(String(item).trim()))
        .filter((item) => Number.isFinite(item));
    }
  }

  return [];
}

function sortItemsByArrayOrder(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const groupsByDate = new Map();
  rows.forEach((row, originalIndex) => {
    const dateKey = getDateScheduledKey(row);
    if (!groupsByDate.has(dateKey)) {
      groupsByDate.set(dateKey, []);
    }
    groupsByDate.get(dateKey).push({ row, originalIndex });
  });

  const sortedDateKeys = Array.from(groupsByDate.keys()).sort((a, b) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return a.localeCompare(b);
  });

  const sortedRows = [];

  for (const dateKey of sortedDateKeys) {
    const dateRows = groupsByDate.get(dateKey) || [];

    let arrayOrder = [];
    for (const entry of dateRows) {
      const candidateOrder = parseArrayOrderValue(entry.row?.arrayOrder ?? entry.row?.array_order);
      if (candidateOrder.length > 0) {
        arrayOrder = candidateOrder;
        break;
      }
    }

    if (arrayOrder.length === 0) {
      sortedRows.push(...dateRows.map((entry) => entry.row));
      continue;
    }

    const indexByPhaseStatusOid = new Map(arrayOrder.map((id, index) => [Number(id), index]));
    const fallbackIndex = Number.MAX_SAFE_INTEGER;

    const sortedDateRows = dateRows
      .slice()
      .sort((a, b) => {
        const aId = Number(getPhaseStatusOid(a.row));
        const bId = Number(getPhaseStatusOid(b.row));
        const aOrder = indexByPhaseStatusOid.has(aId) ? indexByPhaseStatusOid.get(aId) : fallbackIndex;
        const bOrder = indexByPhaseStatusOid.has(bId) ? indexByPhaseStatusOid.get(bId) : fallbackIndex;

        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }

        return a.originalIndex - b.originalIndex;
      })
      .map((entry) => entry.row);

    sortedRows.push(...sortedDateRows);
  }

  return sortedRows;
}

function getPhaseOid(item) {
  return item?.phase_oid ?? item?.phaseOid ?? null;
}

function getLotOid(item) {
  return item?.lot_oid ?? item?.lotOid ?? null;
}

function isSlabPhase(item) {
  const phaseType = item?.phase_type ?? item?.phaseType ?? item?.phase ?? '';
  return String(phaseType).trim().toLowerCase() === 'slab';
}

function isQcTask(item) {
  const qcValue = item?.is_qc ?? item?.isQc ?? 0;
  return Number(qcValue) === 1;
}

function buildLabel(item) {
  const lot = item?.lot || '';
  const address = item?.address ?? item?.Address ?? '';
  const lotWithAddress = address ? `${lot} (${address})` : lot;
  return `${item?.builder || ''} >> ${item?.neighborhood || ''} >> ${lotWithAddress}`.trim();
}

function hasEmployeeStatus(item) {
  const status = item?.employee_status ?? item?.employeeStatus ?? '';
  return String(status).trim().length > 0;
}

function mapEmployeeStatusToComplete(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  if (!status) return '';
  if (status === 'complete' || status === 'success') return 'yes';
  if (status === 'fail') return 'no';
  return '';
}

function getExistingStatusDate(item) {
  return (
    item?.status_date ||
    item?.statusDate ||
    item?.date_complete ||
    item?.dateComplete ||
    ''
  );
}

function toIsoDateString(value) {
  if (!value) return '';

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) {
    return '';
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeHoursInput(value) {
  const raw = String(value ?? '');
  let normalized = raw.replace(',', '.').replace(/[^0-9.]/g, '');
  const dotIndex = normalized.indexOf('.');
  if (dotIndex !== -1) {
    normalized = normalized.slice(0, dotIndex + 1) + normalized.slice(dotIndex + 1).replace(/\./g, '');
  }
  return normalized;
}

function parseHoursValue(value) {
  if (value === '' || value == null) return Number.NaN;
  return Number(String(value).replace(',', '.'));
}

const TeamForms = () => {
  const { employee_oid: employeeOidParam } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const employeeOid = employeeOidParam || searchParams.get('employee_oid') || '';
  const currentPath = String(location.pathname || '').toLowerCase();
  const isPublicMode =
    currentPath === '/teamforms' ||
    currentPath.startsWith('/teamforms/') ||
    currentPath === '/teamforms-public' ||
    currentPath.startsWith('/teamforms-public/');

  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [uploadUserName, setUploadUserName] = useState('unknown');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [downloadingFileOid, setDownloadingFileOid] = useState(null);
  const [inlineImageUrls, setInlineImageUrls] = useState({});
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingExistingFiles, setLoadingExistingFiles] = useState(false);

  const [formValues, setFormValues] = useState({
    status_date: toIsoDateString(new Date()),
    worker_comments: '',
    hours: '',
    complete: '',
    sewer_location: '',
    water_location: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});

  const filesRef = useRef(null);

  const selectedItem = useMemo(() => {
    if (selectedIndex < 0 || selectedIndex >= items.length) return null;
    return items[selectedIndex];
  }, [selectedIndex, items]);

  const groupedItems = useMemo(() => {
    const groups = [];
    const indexByDate = new Map();

    items.forEach((item, index) => {
      const dateKey = getDateScheduledKey(item);
      if (!indexByDate.has(dateKey)) {
        indexByDate.set(dateKey, groups.length);
        groups.push({
          dateKey,
          label: dateKey || 'No Scheduled Date',
          entries: [],
        });
      }

      const groupIndex = indexByDate.get(dateKey);
      groups[groupIndex].entries.push({ item, index });
    });

    return groups;
  }, [items]);
  const qcTask = isQcTask(selectedItem);

  const hasRequiredParam = Number.isFinite(Number(employeeOid)) && Number(employeeOid) > 0;

  const resetForm = () => {
    setFormValues({
      status_date: toIsoDateString(new Date()),
      worker_comments: '',
      hours: '',
      complete: '',
      sewer_location: '',
      water_location: '',
    });
    setFieldErrors({});
    filesRef.current?.clearFiles?.();
  };

  const fetchItems = async () => {
    if (!hasRequiredParam) return;

    setLoading(true);
    setError('');

    try {
      if (isPublicMode) {
        const response = await fetch(`/public-api/teamforms/bootstrap/${Number(employeeOid)}`, {
          method: 'GET',
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to load public team form data.');
        }

        const payload = await response.json();
        const nextItems = Array.isArray(payload.items) ? payload.items : [];
        setEmployeeName(payload.employeeName || 'Unknown Employee');
        setItems(sortItemsByArrayOrder(nextItems));

        if (nextItems.length === 0) {
          setSelectedIndex(-1);
        } else if (selectedIndex >= nextItems.length) {
          setSelectedIndex(-1);
        }
        return;
      }

      const raw = await CentralCalls.callStoredProcedureWithParams('sp_employeeTeamActiveSchedule', {
        employee_oid: Number(employeeOid),
      });
      const normalized = normalizeProcedureResult(raw);
      const nextItems = Array.isArray(normalized) ? normalized : [];
      setItems(sortItemsByArrayOrder(nextItems));
      
      if (nextItems.length === 0) {
        setSelectedIndex(-1);
      } else if (selectedIndex >= nextItems.length) {
        setSelectedIndex(-1);
      }
    } catch (err) {
      console.error(err);
      setItems([]);
      setSelectedIndex(-1);
      setError('Failed to load open schedule items for this employee.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const setUserInfo = async () => {
      if (isPublicMode) {
        setUploadUserName('public-link');
        return;
      }

      try {
        const user = await Auth.currentAuthenticatedUser();
        setUploadUserName(user?.attributes?.email || user?.username || 'unknown');
      } catch (authErr) {
        console.error('Error fetching user info', authErr);
        setUploadUserName('unknown');
      }
    };

    setUserInfo();
  }, [isPublicMode]);

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeOid]);

  useEffect(() => {
    const fetchEmployeeName = async () => {
      if (isPublicMode) {
        return;
      }

      if (!hasRequiredParam) {
        setEmployeeName('');
        return;
      }

      try {
        const employeeRows = await CentralCalls.tableSelect('employee', { oid: Number(employeeOid) });
        if (Array.isArray(employeeRows) && employeeRows.length > 0) {
          setEmployeeName(employeeRows[0]?.name || 'Unknown Employee');
        } else {
          setEmployeeName('Unknown Employee');
        }
      } catch (nameErr) {
        console.error('Failed to load employee name', nameErr);
        setEmployeeName('Unknown Employee');
      }
    };

    fetchEmployeeName();
  }, [employeeOid, hasRequiredParam, isPublicMode]);

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedIndex(-1);
    resetForm();
  };

  const validate = () => {
    const nextErrors = {};
    const qcTask = isQcTask(selectedItem);
    const numericHours = parseHoursValue(formValues.hours);

    if (!formValues.status_date) {
      nextErrors.status_date = 'Date is required.';
    }

    if (!formValues.worker_comments || !formValues.worker_comments.trim()) {
      nextErrors.worker_comments = 'Comments are required.';
    }

    if (formValues.hours === '' || !Number.isFinite(numericHours) || numericHours < 0) {
      nextErrors.hours = 'Hours is required and must be 0 or more.';
    }

    if (!formValues.complete) {
      nextErrors.complete = qcTask ? 'Please select Pass or Fail.' : 'Please select Yes or No.';
    }

    const phaseStatusOid = getPhaseStatusOid(selectedItem);
    if (!phaseStatusOid) {
      nextErrors.row = 'This schedule row is missing phase_status_oid (or oid), so it cannot be updated.';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const uploadFilesForPhase = async (filesToUpload, phaseOid, lotOid) => {
    if (!filesToUpload || filesToUpload.length === 0) {
      return;
    }

    if (!isPublicMode && (!phaseOid || !lotOid)) {
      return;
    }

    setIsUploadingFiles(true);
    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('uploaded_by', uploadUserName || 'unknown');

        let response;
        if (isPublicMode) {
          const phaseStatusOid = getPhaseStatusOid(selectedItem);
          formData.append('phase_status_oid', String(phaseStatusOid || ''));
          response = await fetch(`/public-api/teamforms/files/${Number(employeeOid)}`, {
            method: 'POST',
            body: formData,
          });
        } else {
          formData.append('lot_oid', lotOid);
          response = await fetch('/api/files/upload', {
            method: 'POST',
            body: formData,
          });
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Upload failed for ${file.name}`);
        }

        const uploadedRow = await response.json();
        if (uploadedRow?.oid && !isPublicMode) {
          await CentralCalls.upsertData({ oid: uploadedRow.oid, phase_oid: phaseOid }, 'files', 'update');
        }
      }
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const fetchExistingFiles = async (row) => {
    const targetRow = row || selectedItem;
    const phaseStatusOid = getPhaseStatusOid(targetRow);
    const phaseOid = getPhaseOid(targetRow);
    const lotOid = getLotOid(targetRow);

    if (!targetRow || !phaseStatusOid) {
      setExistingFiles([]);
      return;
    }

    setLoadingExistingFiles(true);
    try {
      if (isPublicMode) {
        const response = await fetch(`/public-api/teamforms/files/${Number(employeeOid)}?phase_status_oid=${Number(phaseStatusOid)}`, {
          method: 'GET',
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to load existing files.');
        }

        const payload = await response.json();
        const files = Array.isArray(payload.files) ? payload.files : [];
        setExistingFiles(files);
      } else {
        if (!phaseOid || !lotOid) {
          setExistingFiles([]);
          return;
        }

        const rows = await CentralCalls.tableSelect('files', {
          phase_oid: Number(phaseOid),
          lot_oid: Number(lotOid),
          is_deleted: 0,
        });

        const files = Array.isArray(rows)
          ? rows
              .slice()
              .sort((a, b) => {
                const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
                const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
                return db - da;
              })
          : [];

        setExistingFiles(files);
      }
    } catch (err) {
      console.error(err);
      setExistingFiles([]);
    } finally {
      setLoadingExistingFiles(false);
    }
  };

  const handleDownloadExistingFile = async (file) => {
    const fileOid = Number(file?.oid);
    if (!Number.isFinite(fileOid) || fileOid <= 0) {
      setError('Unable to download this file because its ID is invalid.');
      return;
    }

    setError('');
    setDownloadingFileOid(fileOid);
    try {
      const endpoint = isPublicMode
        ? `/public-api/teamforms/files/${Number(employeeOid)}/${fileOid}/download`
        : `/api/files/${fileOid}/download`;

      const response = await fetch(endpoint, { method: 'GET' });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to download file.');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      let downloadName = file?.file_name || `file-${fileOid}`;
      const contentDisposition = response.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      if (match && match[1]) {
        downloadName = match[1];
      }

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadErr) {
      console.error(downloadErr);
      setError(downloadErr?.message || 'Failed to download file.');
    } finally {
      setDownloadingFileOid(null);
    }
  };

  const getFileDownloadEndpoint = useCallback((fileOid) => {
    return isPublicMode
      ? `/public-api/teamforms/files/${Number(employeeOid)}/${fileOid}/download`
      : `/api/files/${fileOid}/download`;
  }, [isPublicMode, employeeOid]);

  const isImageFile = (file) => {
    const contentType = String(file?.content_type || '').toLowerCase();
    if (contentType.startsWith('image/')) return true;

    const fileName = String(file?.file_name || '').toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(fileName);
  };

  useEffect(() => {
    let canceled = false;
    const createdUrls = [];

    const loadInlineImages = async () => {
      setInlineImageUrls((prev) => {
        Object.values(prev).forEach((url) => URL.revokeObjectURL(url));
        return {};
      });

      const imageFiles = existingFiles.filter((file) => isImageFile(file));
      if (imageFiles.length === 0) {
        return;
      }

      const results = await Promise.all(
        imageFiles.map(async (file) => {
          const fileOid = Number(file?.oid);
          if (!Number.isFinite(fileOid) || fileOid <= 0) {
            return null;
          }

          try {
            const response = await fetch(getFileDownloadEndpoint(fileOid), { method: 'GET' });
            if (!response.ok) {
              return null;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            return { fileOid, url };
          } catch {
            return null;
          }
        })
      );

      if (canceled) {
        createdUrls.forEach((url) => URL.revokeObjectURL(url));
        return;
      }

      const nextUrls = {};
      results.forEach((result) => {
        if (result?.fileOid && result?.url) {
          nextUrls[result.fileOid] = result.url;
        }
      });

      setInlineImageUrls(nextUrls);
    };

    loadInlineImages();

    return () => {
      canceled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [existingFiles, getFileDownloadEndpoint]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess('');
    setError('');

    if (!selectedItem) {
      setError('Select a scheduled item first.');
      return;
    }

    if (!validate()) {
      return;
    }

    const selectedFiles = filesRef.current?.getFiles?.() || [];
    const phaseStatusOid = getPhaseStatusOid(selectedItem);
    const phaseOid = getPhaseOid(selectedItem);
    const lotOid = getLotOid(selectedItem);
    const isComplete = formValues.complete === 'yes';
    const qcTask = isQcTask(selectedItem);
    const slabPhase = isSlabPhase(selectedItem);
    const nextEmployeeStatus = qcTask
      ? (isComplete ? 'success' : 'fail')
      : (isComplete ? 'complete' : 'fail');
    const numericHours = parseHoursValue(formValues.hours);

    const updateObj = {
      oid: phaseStatusOid,
      status_date: CentralCalls.sqlFormatdate(formValues.status_date),
      worker_comments: formValues.worker_comments.trim(),
      hours: numericHours,
      employee_status: nextEmployeeStatus,
    };

    setSaving(true);
    try {
      if (isPublicMode) {
        const response = await fetch(`/public-api/teamforms/submit/${Number(employeeOid)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phase_status_oid: phaseStatusOid,
            status_date: formValues.status_date,
            worker_comments: formValues.worker_comments.trim(),
            hours: numericHours,
            complete: formValues.complete,
            sewer_location: formValues.sewer_location,
            water_location: formValues.water_location,
          }),
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'Failed to update this phase status row.');
        }
      } else {
      await CentralCalls.upsertData(updateObj, 'phase_status', 'update');
       await CentralCalls.upsertData({oid:phaseOid, 'date_scheduled': CentralCalls.sqlFormatdate(formValues.status_date) }, 'phase', 'update');

      if (!qcTask && slabPhase) {
        if (lotOid) {
          const lotUpdateObj = {
            oid: lotOid,
            sewer_location: formValues.sewer_location ? formValues.sewer_location.trim() : null,
            water_location: formValues.water_location ? formValues.water_location.trim() : null,
          };
          await CentralCalls.upsertData(lotUpdateObj, 'lot', 'update');
        } else {
          alert('Phase status was updated, but lot location updates require lot_oid from the schedule row.');
        }
      }
      }

      if (selectedFiles.length > 0) {
        if (isPublicMode) {
          await uploadFilesForPhase(selectedFiles, phaseOid, lotOid);
        } else if (phaseOid && lotOid) {
          await uploadFilesForPhase(selectedFiles, phaseOid, lotOid);
        } else {
          alert('Phase status was updated, but file upload requires phase_oid and lot_oid from the schedule row.');
        }
      }

      await fetchExistingFiles(selectedItem);

      setSuccess('Phase status updated successfully.');
      handleCloseForm();
      await fetchItems();
    } catch (err) {
      console.error(err);
      setError('Failed to update this phase status row.');
    } finally {
      setSaving(false);
    }
  };

  const onSelectCard = (index) => {
    setSelectedIndex(index);
    setSuccess('');
    setError('');

    const row = items[index] || {};
    const existingComments = row?.worker_comments != null
      ? String(row.worker_comments)
      : (row?.comments != null ? String(row.comments) : '');
    const existingHours = row?.hours != null && row?.hours !== '' ? String(row.hours) : '';
    const existingComplete = mapEmployeeStatusToComplete(row?.employee_status ?? row?.employeeStatus);
    const existingDate = toIsoDateString(getExistingStatusDate(row)) || toIsoDateString(new Date());

    setFormValues({
      status_date: existingDate,
      worker_comments: existingComments,
      hours: existingHours,
      complete: existingComplete,
      sewer_location: row?.sewer_location ?? row?.sewerLocation ?? '',
      water_location: row?.water_location ?? row?.waterLocation ?? '',
    });
    setFieldErrors({});
    filesRef.current?.clearFiles?.();

    fetchExistingFiles(row);

    setFormOpen(true);
  };

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#B3CDE6', color: '#111', borderBottom: '1px solid #d7e2ec' }}>
        <Toolbar sx={{ minHeight: '64px !important', justifyContent: 'center', gap: 1.25 }}>
          <Box component="img" src={logo} alt="Cowart" sx={{ width: 42, height: 42, objectFit: 'contain' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
           Active Team Schedules
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 2 } }}>
        <Stack spacing={2}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Scheduled Phases</Typography>
            <Typography variant="body1" sx={{ color: '#22313f', mt: 0.8, fontWeight: 600 }}>
              {employeeName || (hasRequiredParam ? 'Loading employee...' : 'No employee selected')}
            </Typography>
          </Paper>

          {!hasRequiredParam && (
            <Alert severity="error">
                This page requires employee_oid in the URL. Example: /teamForms/123 or /teamForms?employee_oid=123
            </Alert>
          )}

          {error ? <Alert severity="error">{error}</Alert> : null}
          {success ? <Alert severity="success">{success}</Alert> : null}

          <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Open Scheduled Items</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={fetchItems}
                disabled={!hasRequiredParam || loading || saving}
                sx={{ textTransform: 'none' }}
              >
                Refresh
              </Button>
            </Stack>

            {loading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : items.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#526270' }}>
                No open scheduled items were returned.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {groupedItems.map((group) => (
                  <Stack key={`group-${group.dateKey || 'none'}`} spacing={1}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#22313f' }}>
                      {group.label}
                    </Typography>

                    <Stack spacing={1.25}>
                      {group.entries.map(({ item, index }) => {
                        const completedByEmployee = hasEmployeeStatus(item);
                        return (
                          <Button
                            key={`${buildLabel(item)}-${index}`}
                            variant="outlined"
                            onClick={() => onSelectCard(index)}
                            sx={{
                              justifyContent: 'flex-start',
                              textAlign: 'left',
                              textTransform: 'none',
                              borderRadius: 2,
                              p: 1.25,
                              backgroundColor: completedByEmployee ? '#BBDEFB' : '#C8E6C9',
                              borderColor: completedByEmployee ? '#64B5F6' : '#81C784',
                              '&:hover': {
                                backgroundColor: completedByEmployee ? '#90CAF9' : '#A5D6A7',
                                borderColor: completedByEmployee ? '#42A5F5' : '#66BB6A',
                              },
                            }}
                          >
                            <Box>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{buildLabel(item)}</Typography>
                              <Typography sx={{ fontSize: '0.82rem', opacity: 0.95 }}>
                                {item?.phase || 'Unknown phase'}
                                {item?.assignedTeam ? ` | Team: ${item.assignedTeam}` : ''}
                              </Typography>
                            </Box>
                          </Button>
                        );
                      })}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Stack>
      </Container>

      <Dialog
        open={formOpen}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Finish Task</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {selectedItem && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {buildLabel(selectedItem)} {selectedItem?.phase ? `| ${selectedItem.phase}` : ''}
              </Alert>
            )}

            {fieldErrors.row ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {fieldErrors.row}
              </Alert>
            ) : null}

            <Stack spacing={1.5}>
              <TextField
                fullWidth
                type="date"
                label="Date *"
                value={formValues.status_date}
                onChange={(e) => setFormValues((prev) => ({ ...prev, status_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldErrors.status_date)}
                helperText={fieldErrors.status_date || ''}
                inputProps={{ max: '2099-12-31' }}
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Comments *"
                value={formValues.worker_comments}
                onChange={(e) => setFormValues((prev) => ({ ...prev, worker_comments: e.target.value }))}
                error={Boolean(fieldErrors.worker_comments)}
                helperText={fieldErrors.worker_comments || ''}
              />

              <TextField
                fullWidth
                type="text"
                label="Hours *"
                value={formValues.hours}
                onChange={(e) => setFormValues((prev) => ({ ...prev, hours: normalizeHoursInput(e.target.value) }))}
                error={Boolean(fieldErrors.hours)}
                helperText={fieldErrors.hours || ''}
                inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' }}
              />

              <FormControl error={Boolean(fieldErrors.complete)}>
                <FormLabel>{qcTask ? 'Status *' : 'Complete *'}</FormLabel>
                <RadioGroup
                  row
                  value={formValues.complete}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, complete: e.target.value }))}
                >
                  <FormControlLabel value="yes" control={<Radio />} label={qcTask ? 'Pass *' : 'Yes'} />
                  <FormControlLabel value="no" control={<Radio />} label={qcTask ? 'Fail *' : 'No'} />
                </RadioGroup>
                {fieldErrors.complete ? (
                  <Typography variant="caption" color="error">{fieldErrors.complete}</Typography>
                ) : null}
              </FormControl>

              {!qcTask && isSlabPhase(selectedItem) ? (
                <>
                  <TextField
                    fullWidth
                    label="Sewer Location"
                    value={formValues.sewer_location}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, sewer_location: e.target.value }))}
                  />

                  <TextField
                    fullWidth
                    label="Water Location"
                    value={formValues.water_location}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, water_location: e.target.value }))}
                  />
                </>
              ) : null}

              <FileUpload
                ref={filesRef}
                inputId="team-forms-files"
                title="Attach Files"
                helperText="Choose from phone files or gallery"
                isUploading={isUploadingFiles}
                accept="image/*,.pdf,.png,.jpg,.jpeg,.heic,.heif"
              />

              <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.75 }}>Existing Files</Typography>
                {loadingExistingFiles ? (
                  <Typography variant="body2" sx={{ color: '#526270' }}>
                    Loading files...
                  </Typography>
                ) : existingFiles.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#526270' }}>
                    No active files uploaded yet.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {existingFiles.map((file) => {
                      const fileOid = file?.oid;
                      const fileName = file?.file_name || `File ${fileOid}`;
                      const createdAt = file?.created_at ? toIsoDateString(file.created_at) : '';

                      return (
                        <Box key={String(fileOid)} sx={{ border: '1px solid #d7e2ec', borderRadius: 1.5, p: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ color: '#0b426b', fontWeight: 600 }}>
                              {fileName}
                            </Typography>
                            {createdAt ? (
                              <Typography variant="caption" sx={{ color: '#526270' }}>
                                {createdAt}
                              </Typography>
                            ) : null}
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleDownloadExistingFile(file)}
                              disabled={downloadingFileOid === fileOid}
                              sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}
                            >
                              {downloadingFileOid === fileOid ? 'Downloading...' : 'Download'}
                            </Button>
                          </Box>

                          {inlineImageUrls[fileOid] ? (
                            <Box
                              component="img"
                              src={inlineImageUrls[fileOid]}
                              alt={fileName}
                              sx={{
                                display: 'block',
                                mt: 1,
                                width: '100%',
                                maxWidth: 360,
                                maxHeight: 220,
                                objectFit: 'contain',
                                borderRadius: 1,
                                border: '1px solid #e3edf7',
                                backgroundColor: '#f7fbff',
                              }}
                            />
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!selectedItem || saving || loading || isUploadingFiles}
                  sx={{ textTransform: 'none', py: 1.1 }}
                >
                  {saving ? 'Saving...' : (qcTask ? 'COMPLETE' : 'Save')}
                </Button>

                <Button
                  fullWidth
                  type="button"
                  variant="outlined"
                  onClick={handleCloseForm}
                  disabled={saving || isUploadingFiles}
                  sx={{ textTransform: 'none', py: 1.1 }}
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default TeamForms;
